import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import type { ProjectId } from '@/shared/types/common.types';
import { workspaceService } from '../services/workspaceService';
import type {
  CreateProjectInput,
  UpdateProjectInput,
  CursorPage,
  ProjectListPage,
  Tenant,
  WorkspaceProject,
} from '../types/workspace.types';
import { workspaceKeys } from './workspaceKeys';

/**
 * The caller's projects, a cursor page at a time.
 *
 * `enabled` lets a page wait for the capability check, so a Core without the workspace
 * never receives a request for it.
 */
export function useWorkspaceProjects(
  search: string,
  limit: number,
  enabled: boolean
): UseInfiniteQueryResult<InfiniteData<ProjectListPage, string | undefined>> {
  return useInfiniteQuery({
    queryKey: workspaceKeys.projects(search, limit),
    queryFn: ({ pageParam }) =>
      workspaceService.listProjects({
        limit,
        ...(search.length > 0 ? { q: search } : {}),
        ...(pageParam === undefined ? {} : { cursor: pageParam }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    // Keep the current rows while a new search loads, instead of flashing a spinner.
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useWorkspaceProject(
  projectId: ProjectId,
  enabled: boolean
): UseQueryResult<WorkspaceProject> {
  return useQuery({
    queryKey: workspaceKeys.project(projectId),
    queryFn: () => workspaceService.getProject(projectId),
    enabled,
  });
}

export function useTenants(enabled: boolean): UseQueryResult<CursorPage<Tenant>> {
  return useQuery({
    queryKey: workspaceKeys.tenants,
    queryFn: () => workspaceService.listTenants(),
    enabled,
  });
}

export function useCreateProject(): UseMutationResult<
  WorkspaceProject,
  Error,
  { readonly input: CreateProjectInput; readonly idempotencyKey: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ input, idempotencyKey }) => workspaceService.createProject(input, idempotencyKey),
    onSuccess: (project) => {
      queryClient.setQueryData(workspaceKeys.project(project.id), project);
      void queryClient.invalidateQueries({ queryKey: ['workspace', 'projects'] });
    },
  });
}

/**
 * Renames a project or changes its code.
 *
 * The project is read first, so the `If-Match` token is the current one rather than whatever the
 * list was showing; a change made by someone else in between still gets a `412`.
 */
export function useUpdateProject(): UseMutationResult<
  WorkspaceProject,
  Error,
  { readonly projectId: ProjectId; readonly input: UpdateProjectInput }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, input }) => {
      const current = await workspaceService.getProject(projectId);
      return workspaceService.updateProject(projectId, input, current.etag);
    },
    onSuccess: (project) => {
      queryClient.setQueryData(workspaceKeys.project(project.id), project);
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
}

export function useDeleteProject(): UseMutationResult<void, Error, ProjectId> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId) => {
      const current = await workspaceService.getProject(projectId);
      await workspaceService.deleteProject(projectId, current.etag);
    },
    onSuccess: (_void, projectId) => {
      queryClient.removeQueries({ queryKey: workspaceKeys.project(projectId) });
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
}
